import { useState, useEffect, useRef, useMemo } from "react";
import Favorites from "./Favorites";
import ControlModal from "./ControlModal";
import { ChevronDown, ChevronUp, Plus, Moon, Sun, Languages } from "lucide-react";
import { t as i18n } from "../i18n";

export default function ActionPanel({
  theme,
  language,
  filters,
  onToggleLine,
  areAllSelected,
  onToggleAll,
  onThemeToggle,
  onLanguageToggle, // should accept code?: "kn" | "hi" | "en"
}) {
  const [open, setOpen] = useState(true);
  const [fabOpen, setFabOpen] = useState(false);
  const [showControlModal, setShowControlModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [dragHeight, setDragHeight] = useState(60);
  const [isDragging, setIsDragging] = useState(false);
  const [isFavOpen, setIsFavOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false); // <— language radial open/close
  const startY = useRef(0);
  const startHeight = useRef(60);

  const isDark = theme === "dark";
  const central = i18n(language);

  useEffect(() => {
    if (!isMobile) return;
    const threshold = window.innerHeight * 0.2;
    setIsFavOpen(dragHeight > threshold);
  }, [dragHeight, isMobile]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      // if the click is inside a language button or the main Languages FAB, ignore it
      if (e.target.closest(".lang-cluster")) return;
      setLangOpen(false);
    };

    if (langOpen) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }

    return () => document.removeEventListener("click", handleClickOutside);
  }, [langOpen]);

  // --- drag bottom-sheet logic
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    setIsDragging(true);
    startY.current = e.touches[0].clientY;
    startHeight.current = dragHeight;
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const deltaY = startY.current - e.touches[0].clientY;
    let newH = startHeight.current + deltaY;
    newH = Math.max(60, Math.min(window.innerHeight * 0.7, newH));
    setDragHeight(newH);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    const threshold = window.innerHeight * 0.4;
    setDragHeight(dragHeight > threshold ? window.innerHeight * 0.7 : 60);
  };

  const toggleHeaderTap = () => {
    if (!isMobile) return;
    setDragHeight((h) => (h <= 80 ? window.innerHeight * 0.7 : 60));
  };

  // --- styles
  const panelStyle = {
    position: "fixed",
    bottom: 0,
    left: 0,
    width: "100%",
    height: isMobile ? `${dragHeight}px` : open ? "200px" : "42px",
    background: isDark ? "#1a1a1a" : "#fff",
    color: isDark ? "#fff" : "#111",
    borderTop: isDark ? "1px solid #333" : "1px solid #e5e5e5",
    transition: isDragging ? "none" : "height 0.3s ease-in-out",
    zIndex: 2500,
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 -2px 10px rgba(0,0,0,0.08)",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: "hidden",
    touchAction: "none",
  };

  const headerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0 14px",
    background: isDark ? "#1f1f1f" : "#fafafa",
    borderBottom: isDark ? "1px solid #333" : "1px solid #eee",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    height: 42,
    cursor: isMobile ? "grab" : "default",
  };

  const fabContainer = useMemo(
    () => ({
      position: "fixed",
      bottom: isMobile ? dragHeight + 20 : open ? 220 : 70,
      right: 25,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 10,
      zIndex: 2600,
      transition: "bottom 0.3s ease",
    }),
    [isMobile, dragHeight, open]
  );

  const fabBtn = {
    width: 50,
    height: 50,
    borderRadius: "50%",
    background: "#1976d2",
    color: "#fff",
    border: "none",
    boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s ease",
  };

  // helper for radial positions (semi-circle up/left)
  const langButtons = [
    { code: "kn", label: "ಕ", title: "Kannada", angleDeg: 210 },
    { code: "hi", label: "ह", title: "Hindi", angleDeg: 180 },
    { code: "en", label: "EN", title: "English", angleDeg: 150 },
  ];
  const radius = 65; // distance from center of main language FAB

  return (
    <>
      {/* Bottom Favorites Panel */}
      <div
        style={panelStyle}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          style={{
            ...headerStyle,
            flexDirection: "column",
            height: "auto",
            padding: "2px 0 10px 0",
            cursor: "pointer",
          }}
          onClick={() => {
            if (isMobile) toggleHeaderTap();
            else setOpen((prev) => !prev);
          }}
        >
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {isMobile ? (
              dragHeight > 80 ? (
                <ChevronDown size={20} />
              ) : (
                <ChevronUp size={20} />
              )
            ) : open ? (
              <ChevronDown size={20} />
            ) : (
              <ChevronUp size={20} />
            )}
          </div>

          <h4
            style={{
              margin: "4px 0 0 0",
              textAlign: "center",
              fontWeight: 600,
              letterSpacing: 0.5,
              lineHeight: 0.3,
            }}
          >
            {central.favourites}
          </h4>
        </div>

        {/* Scrollable Favorites on mobile */}
        {(open || isMobile) && (
          <div
            style={{
              flex: 1,
              padding: 12,
              overflowY: isMobile ? "auto" : "visible",
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 12,
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Favorites theme={theme} language={language} />
          </div>
        )}
      </div>

      {/* Floating Action Buttons */}
      {!isFavOpen && (
        <div style={fabContainer}>
          <div
            style={{
              position: "relative",
              width: 50,
              height: fabOpen ? 260 : 50,
              transition: "height 0.3s ease",
              display: "flex",
              flexDirection: "column-reverse",
              alignItems: "center",
              pointerEvents: "none",
            }}
          >
            {/* Main FAB */}
            <button
              style={{
                ...fabBtn,
                transform: fabOpen ? "rotate(45deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
                pointerEvents: "auto",
                zIndex: 5,
              }}
              onClick={() => {
                setFabOpen(!fabOpen);
                setLangOpen(false); // close radial when collapsing
              }}
              aria-expanded={fabOpen}
              aria-label="Actions"
              title="Actions"
            >
              <Plus size={24} />
            </button>

            {/* Controls: colored hamburger */}
            <div
              style={{
                position: "absolute",
                bottom: fabOpen ? 70 : 0,
                opacity: fabOpen ? 1 : 0,
                transition: "all 0.3s ease",
              }}
            >
              <button
                style={{
                  ...fabBtn,
                  background: isDark ? "#6e6b6bff" : "#ffffff",
                  border: isDark ? "1px solid #3a3a3a" : "1px solid #e5e7eb",
                  pointerEvents: "auto",
                }}
                onClick={() => setShowControlModal(true)}
                title="Controls"
                aria-label="Open Controls"
              >
                <svg width="28" height="28" viewBox="0 0 24 24" role="img" aria-label="Controls">
                  <rect x="3" y="6" width="18" height="5" rx="1" fill="#1dcf5fff" />
                  <rect x="3" y="11" width="18" height="5" rx="1" fill="#932df1ff" />
                  <rect x="3" y="16" width="18" height="5" rx="1" fill="#f8bc06ff" />
                </svg>
              </button>
            </div>

            {/* Theme: Sun/Moon */}
            <div
              style={{
                position: "absolute",
                bottom: fabOpen ? 130 : 0,
                opacity: fabOpen ? 1 : 0,
                transition: "all 0.35s ease",
              }}
            >
              <button
                style={{
                  ...fabBtn,
                  background: isDark ? "#6e6b6bff" : "#fdfdfdff", // dark = gray bg, light = blue bg
                  color: isDark ? "#f1f3f7ff" : "#000000ff",      // dark = gray icon, light = white icon
                  border: isDark ? "1px solid #000000ff" : "1px solid #ffffff",
                  pointerEvents: "auto",
                }}
                onClick={onThemeToggle}
                title={isDark ? "Switch to Light" : "Switch to Dark"}
                aria-label={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {isDark ? <Sun size={20} /> : <Moon size={20} />}
              </button>
            </div>

            {/* Language: FAB that expands into a semi-circle */}
            <div
              style={{
                position: "absolute",
                bottom: fabOpen ? 190 : 0,
                left: 0,
                width: 50,
                height: 50,
                opacity: fabOpen ? 1 : 0,
                transition: "all 0.4s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* Click-to-open wrapper for satellites */}
              <div
                style={{
                  position: "relative",
                  width: 120,
                  height: 120,
                  pointerEvents: "auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Satellites (KN/HI/EN) */}
                {langButtons.map(({ code, label, title, angleDeg }) => {
                  const rad = (angleDeg * Math.PI) / 180;
                  const x = Math.cos(rad) * radius;
                  const y = Math.sin(rad) * radius;
                  return (
                    <button
                      key={code}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (typeof onLanguageToggle === "function") onLanguageToggle(code);
                        setLangOpen(false);
                      }}
                      title={title}
                      aria-label={`Switch to ${title}`}
                      aria-pressed={language === code}
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: langOpen
                          ? `translate(${x - 18}px, ${y - 18}px) scale(1)`
                          : "translate(-18px, -18px) scale(0.2)",
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        border: isDark ? "1px solid #2f2f2f" : "1px solid #ddd",
                        background: (() => {
                          if (isDark) {
                            return language === code ? "#ffffff" : "#2f2f2f";
                          } else {
                            return language === code ? "#000000" : "#ffffff";
                          }
                        })(),
                        color: (() => {
                          if (isDark) {
                            return language === code ? "#000000" : "#ffffff";
                          } else {
                            return language === code ? "#ffffff" : "#000000";
                          }
                        })(),
                        fontWeight: 700,
                        fontSize: 12,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        boxShadow:
                          language === code
                            ? "0 0 0 2px rgba(255,255,255,0.25)"
                            : "0 2px 6px rgba(0,0,0,0.15)",
                        opacity: langOpen ? 1 : 0,
                        transition:
                          "transform 250ms ease, opacity 200ms ease, background 200ms ease, color 200ms ease",
                        pointerEvents: langOpen ? "auto" : "none",
                      }}
                    >
                      {label}
                    </button>
                  );
                })}

                {/* Main language FAB (center of cluster) */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setLangOpen((v) => !v);
                  }}
                  title="Change Language"
                  aria-label="Change Language"
                  style={{
                    ...fabBtn,
                    background: isDark ? "#6e6b6bff" : "#ffffff",
                    border: isDark ? "1px solid #3a3a3a" : "1px solid #e5e7eb",
                    color: isDark ? "#e5e7eb" : "#111827",
                    position: "relative",
                    zIndex: 2,
                  }}
                >
                  <Languages size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Popup Modal */}
      {showControlModal && (
        <div
          onClick={() => setShowControlModal(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2700,
            transition: "opacity 0.3s ease",
          }}
        >
          <ControlModal
            theme={theme}
            language={language}
            filters={filters}
            onToggleLine={onToggleLine}
            areAllSelected={areAllSelected}
            onToggleAll={onToggleAll}
            onClose={() => setShowControlModal(false)}
          />
        </div>
      )}
    </>
  );
}
